import { nanoid } from 'nanoid';
import { OTHER_ASSET_TYPE } from '~/constants/template';
import { TaskInput } from '~/generated/graphql';
import { JobTemplateFieldsFragment } from '~/gql/graphql';
import { taskTemplateToTaskInput } from './CreateJobForm/taskTemplateToTaskInput';

export type TaskPreviewAsset = {
  id: string;
  name: string;
  type: string;
  deployment?: {
    space?: { name?: string | null } | null;
  } | null;
};

export type TaskPreview = {
  taskName: string;
  taskType: string;
  assetId: string;
  assetName: string;
  assetType: string;
  /** The task template that generated this task */
  templateTaskId: string;
  /** Reference to the full template task to preserve all fields (description, attributes, etc.) */
  templateTask: JobTemplateFieldsFragment['tasks'][number];
  /** Whether this task was created using the "Other" rule (fallback for unmatched asset types) */
  usedOtherRule?: boolean;
};

export type AssetMatch = {
  asset: TaskPreviewAsset;
  matchingTasks: TaskPreview[];
  /** Whether this asset will receive any tasks */
  hasMatchingTasks: boolean;
};

/**
 * Matches assets against template task rules
 *
 * For each task template in weight order:
 * - Match against selected assets based on task config (assetTypes, spaces, etc.)
 * - Create task preview for each matching asset
 * - If an asset doesn't match any rule, use the "Other" rule if it exists
 */
export const calculateTaskPreviews = (
  template: JobTemplateFieldsFragment,
  selectedAssets: TaskPreviewAsset[]
): { tasks: TaskPreview[]; assetMatches: AssetMatch[] } => {
  const tasks: TaskPreview[] = [];
  const assetMatchMap = new Map<string, TaskPreview[]>();
  // Track Asset-type task matches separately for "Other" rule fallback logic
  const assetRuleMatchMap = new Map<string, TaskPreview[]>();

  // Initialize asset match maps
  selectedAssets.forEach((asset) => {
    assetMatchMap.set(asset.id, []);
    assetRuleMatchMap.set(asset.id, []);
  });

  // Separate "Other" rule from other rules
  const otherRule = template.tasks.find((task) => {
    if (task.type !== 'Asset' || !task.config) return false;
    return (
      task.config.assetTypes?.includes(OTHER_ASSET_TYPE) &&
      (!task.config.spaces || task.config.spaces.length === 0)
    );
  });

  const nonOtherRules = template.tasks.filter((task) => {
    if (task.type !== 'Asset' || !task.config) return true;
    return !(
      task.config.assetTypes?.includes(OTHER_ASSET_TYPE) &&
      (!task.config.spaces || task.config.spaces.length === 0)
    );
  });

  // First, match against all non-"Other" rules
  nonOtherRules.forEach((templateTask) => {
    // Only process Asset-type tasks for asset-driven templates
    if (templateTask.type !== 'Asset') {
      // For non-asset tasks, create one task per selected asset
      // These are added to assetMatchMap but NOT to assetRuleMatchMap
      // because they shouldn't influence "Other" rule fallback or uncovered-assets logic
      selectedAssets.forEach((asset) => {
        const preview: TaskPreview = {
          taskName: templateTask.name,
          taskType: templateTask.type,
          assetId: asset.id,
          assetName: asset.name,
          assetType: asset.type,
          templateTaskId: templateTask.id,
          templateTask,
        };
        tasks.push(preview);
        assetMatchMap.get(asset.id)?.push(preview);
        // Note: Not added to assetRuleMatchMap - only Asset-type tasks count for rule matching
      });
      return;
    }

    // For Asset tasks, match based on config rules
    const config = templateTask.config;
    if (!config) {
      // No config means match all selected assets
      selectedAssets.forEach((asset) => {
        const preview: TaskPreview = {
          taskName: `${templateTask.name} - ${asset.name}`,
          taskType: templateTask.type,
          assetId: asset.id,
          assetName: asset.name,
          assetType: asset.type,
          templateTaskId: templateTask.id,
          templateTask,
        };
        tasks.push(preview);
        assetMatchMap.get(asset.id)?.push(preview);
        assetRuleMatchMap.get(asset.id)?.push(preview);
      });
      return;
    }

    // Match assets based on config rules
    selectedAssets.forEach((asset) => {
      let matches = true;

      // Check asset type filter
      if (config.assetTypes && config.assetTypes.length > 0) {
        matches = config.assetTypes.includes(asset.type);
      }

      // Check space filter (if asset has deployment with space)
      if (matches && config.spaces && config.spaces.length > 0) {
        const assetSpace = asset.deployment?.space?.name;
        matches = assetSpace ? config.spaces.includes(assetSpace) : false;
      }

      if (matches) {
        const preview: TaskPreview = {
          taskName: `${templateTask.name} - ${asset.name}`,
          taskType: templateTask.type,
          assetId: asset.id,
          assetName: asset.name,
          assetType: asset.type,
          templateTaskId: templateTask.id,
          templateTask,
        };
        tasks.push(preview);
        assetMatchMap.get(asset.id)?.push(preview);
        assetRuleMatchMap.get(asset.id)?.push(preview);
      }
    });
  });

  // Then, for assets that don't match any Asset-type rule, use the "Other" rule if it exists
  if (otherRule && otherRule.config) {
    selectedAssets.forEach((asset) => {
      // Only use "Other" rule if asset hasn't matched any Asset-type rule
      // Use assetRuleMatchMap (not assetMatchMap) to exclude non-Asset tasks
      const hasAssetRuleMatches =
        (assetRuleMatchMap.get(asset.id)?.length || 0) > 0;
      if (!hasAssetRuleMatches) {
        const preview: TaskPreview = {
          taskName: `${otherRule.name} - ${asset.name}`,
          taskType: otherRule.type,
          assetId: asset.id,
          assetName: asset.name,
          assetType: asset.type,
          templateTaskId: otherRule.id,
          templateTask: otherRule,
          usedOtherRule: true,
        };
        tasks.push(preview);
        assetMatchMap.get(asset.id)?.push(preview);
        assetRuleMatchMap.get(asset.id)?.push(preview);
      }
    });
  }

  // Build asset matches array
  // hasMatchingTasks should only consider Asset-type tasks for uncovered-assets logic
  const assetMatches: AssetMatch[] = selectedAssets.map((asset) => {
    const allMatchingTasks = assetMatchMap.get(asset.id) || [];
    const assetRuleMatches = assetRuleMatchMap.get(asset.id) || [];
    return {
      asset,
      matchingTasks: allMatchingTasks,
      // Only Asset-type rule matches count for determining if asset is "covered"
      // This ensures assets with only non-Asset tasks (e.g., Custom tasks) still show as uncovered
      // if they don't have any Asset-type tasks
      hasMatchingTasks: assetRuleMatches.length > 0,
    };
  });

  return { tasks, assetMatches };
};

/**
 * Helper hook to generate tasks from asset-driven template
 * Used when creating the actual job
 *
 * Preserves all template task fields (description, attributes, etc.)
 * and properly configures asset-specific fields.
 */
export const generateTasksFromAssetDrivenTemplate = (
  template: JobTemplateFieldsFragment,
  selectedAssets: TaskPreviewAsset[]
): Array<TaskInput & { assetId: string; templateTaskId: string }> => {
  const { tasks } = calculateTaskPreviews(template, selectedAssets);

  return tasks.map((preview) => {
    // Convert template task to TaskInput format, preserving all fields
    const baseTask = taskTemplateToTaskInput(preview.templateTask);

    // For Asset tasks, ensure the asset ID is set correctly and selection is always 'specific'
    // Asset-driven templates always create tasks with specific assets, not "all matching assets"
    if (preview.taskType === 'Asset' && baseTask.asset) {
      baseTask.asset = {
        ...baseTask.asset,
        selection: 'specific', // Always use specific selection for asset-driven templates
        assets: [preview.assetId],
      };
    }

    return {
      ...baseTask,
      id: nanoid(),
      name: preview.taskName,
      // Add extra fields for tracking
      assetId: preview.assetId,
      templateTaskId: preview.templateTaskId,
    } as TaskInput & { assetId: string; templateTaskId: string };
  });
};
